import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import { applyAuthSessionFromResponse, isCookieAuthEnabled, isInsecureHttpOrigin } from '../utils/sessionAuth';
import { clearClientAuthSession } from '../utils/clearClientAuthSession';
import { queryClient } from '../lib/queryClient';
import { persistAdminPortalSession } from '../utils/adminPortalSession';
import { persistPartnerPortalSession } from '../utils/partnerPortalSession';
import { POST_LOGIN_ROUTES } from '../utils/resolvePostLoginDestination';
import {
  AccountPathway,
  getStoredAccountPathway,
  setStoredAccountPathway,
} from '../constants/accountTypes';
import { notifyAuthTokenChanged } from '../constants/authEvents';
import { clearApiRequestCache } from '../services/apiRequestCache';
import { resolveOtpChannels, type OtpChannels } from '../utils/otpChannels';

export interface MSMERegistrationData {
  companyName: string;
  companyType: string;
  industry: string;
  businessDomain: string;
  establishmentYear?: number;
  udyamRegistrationNumber: string;
  gstNumber: string;
  panNumber: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  annualTurnover?: number;
  numberOfEmployees?: number;
  manufacturingUnits?: number;
  primaryProducts: string;
  hasEnvironmentalClearance: boolean;
  hasPollutionControlBoard: boolean;
  hasWasteManagement: boolean;
  manufacturingProfile?: {
    msmeType?: string;
    industrySector?: string;
    nicCode?: string;
    yearOfEstablishment?: number;
    locationCity?: string;
    locationState?: string;
    locationCountry?: string;
    numberOfEmployees?: number;
    plantAreaSqft?: number;
    operationalDaysPerYear?: number;
    primaryEnergySource?: string;
    backupEnergySource?: string;
    mainFuelsUsed?: string[];
    waterSource?: string;
    wasteManagementPractice?: string;
    keyProducts?: string[];
    productionCapacityPerMonth?: number;
    productionCapacityUnit?: string;
    supplyChainType?: string;
    logisticsMode?: string;
    certifications?: string[];
    esgMaturityLevel?: string;
    digitalizationLevel?: string;
    carbonAccountingPractice?: string;
    regulatoryExposure?: string[];
    exportActivity?: boolean;
    clusterAssociation?: string;
  };
  agreeToTerms: boolean;
  agreeToDataProcessing: boolean;
}

interface LogoutOptions {
  redirectTo?: string;
}

export type LoginOtpChannels = OtpChannels;

export type LoginResult =
  | { status: 'authenticated'; redirectPath: string | null }
  | {
      status: 'login_otp_required';
      sessionId: string;
      channels: LoginOtpChannels;
      message?: string;
    }
  | { status: 'mfa_required'; mfaToken: string; message?: string }
  | { status: 'mfa_enrollment_required'; enrollmentToken: string; message?: string };

interface RegistrationContextValue {
  isRegistered: boolean;
  isLoggingOut: boolean;
  hasCompletedRegistration: boolean;
  registrationData: MSMERegistrationData | null;
  accountPathway: AccountPathway | null;
  userRole: string | null;
  completeRegistration: (data: MSMERegistrationData, token?: string) => void;
  login: (email: string, password: string, pathway?: AccountPathway) => Promise<LoginResult>;
  completeLoginFromResponse: (response: any) => Promise<LoginResult>;
  setAccountPathway: (pathway: AccountPathway) => void;
  logout: (options?: LogoutOptions) => void;
  resetRegistration: () => void;
}

const RegistrationContext = createContext<RegistrationContextValue | undefined>(undefined);

const getStoredRegistrationData = (): MSMERegistrationData | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = localStorage.getItem('msmeRegistration');
    return raw ? (JSON.parse(raw) as MSMERegistrationData) : null;
  } catch (error) {
    console.warn('Unable to access stored MSME registration data.', error);
    return null;
  }
};

const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    if (isCookieAuthEnabled()) {
      return 'cookie-session';
    }
    return localStorage.getItem('token');
  } catch (error) {
    console.warn('Unable to access stored authentication token.', error);
    return null;
  }
};

const clearStoredAuthState = () => {
  clearClientAuthSession();
};

const mapMsmeToRegistrationData = (msme: any): MSMERegistrationData | null => {
  if (!msme) {
    return null;
  }

  return {
    companyName: msme.companyName ?? '',
    companyType: msme.companyType ?? '',
    industry: msme.industry ?? '',
    businessDomain: msme.businessDomain ?? '',
    establishmentYear: msme.establishmentYear ?? undefined,
    udyamRegistrationNumber: msme.udyamRegistrationNumber ?? '',
    gstNumber: msme.gstNumber ?? '',
    panNumber: msme.panNumber ?? '',
    email: msme.contact?.email ?? '',
    phone: msme.contact?.phone ?? '',
    address: msme.contact?.address?.street ?? '',
    city: msme.contact?.address?.city ?? '',
    state: msme.contact?.address?.state ?? '',
    pincode: msme.contact?.address?.pincode ?? '',
    country: msme.contact?.address?.country ?? 'India',
    annualTurnover: msme.business?.annualTurnover ?? undefined,
    numberOfEmployees: msme.business?.numberOfEmployees ?? undefined,
    manufacturingUnits: msme.business?.manufacturingUnits ?? undefined,
    primaryProducts: msme.business?.primaryProducts ?? '',
    hasEnvironmentalClearance: msme.environmentalCompliance?.hasEnvironmentalClearance ?? false,
    hasPollutionControlBoard: msme.environmentalCompliance?.hasPollutionControlBoard ?? false,
    hasWasteManagement: msme.environmentalCompliance?.hasWasteManagement ?? false,
    manufacturingProfile: msme.manufacturingProfile ?? undefined,
    agreeToTerms: true,
    agreeToDataProcessing: true,
  };
};

export const RegistrationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [registrationData, setRegistrationData] = useState<MSMERegistrationData | null>(
    getStoredRegistrationData
  );
  const [isRegistered, setIsRegistered] = useState<boolean>(() => Boolean(getStoredToken()));
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [accountPathway, setAccountPathwayState] = useState<AccountPathway | null>(
    getStoredAccountPathway
  );
  const [userRole, setUserRole] = useState<string | null>(null);

  React.useEffect(() => {
    const validateStoredToken = async () => {
      const token = getStoredToken();
      if (!token) {
        return;
      }

      try {
        const response = await ApiService.request('/auth/me');
        if (!response?.success) {
          clearStoredAuthState();
          setIsRegistered(false);
          return;
        }
        const role = response?.data?.user?.role as string | undefined;
        if (role) {
          setUserRole(role);
          applyAuthSessionFromResponse({ useCookieAuth: isCookieAuthEnabled(), user: { role } });
          if (role === 'enterprise') {
            setAccountPathwayState('enterprise');
            setStoredAccountPathway('enterprise');
          } else if (role === 'msme') {
            setAccountPathwayState('msme');
            setStoredAccountPathway('msme');
          }
        }
      } catch (error) {
        clearStoredAuthState();
        setIsRegistered(false);
      }
    };

    validateStoredToken();
  }, []);

  const hasCompletedRegistration = useMemo(
    () => Boolean(registrationData),
    [registrationData]
  );

  const persistRegistrationData = useCallback((data: MSMERegistrationData | null) => {
    setRegistrationData(data);

    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (data) {
        localStorage.setItem('msmeRegistration', JSON.stringify(data));
      } else {
        localStorage.removeItem('msmeRegistration');
      }
    } catch (error) {
      console.warn('Unable to persist MSME registration data.', error);
    }
  }, []);

  // Cross-tab logout / login sync (DEEP-BUG-005): another tab clearing `token`
  // must immediately drop workspace UI in this tab.
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncAuthFromOtherTab = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) {
        return;
      }
      if (event.key !== 'token' && event.key !== null) {
        return;
      }

      const token = getStoredToken();
      if (!token) {
        clearStoredAuthState();
        setIsRegistered(false);
        setUserRole(null);
        persistRegistrationData(null);
        if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
          navigate('/login', { replace: true });
        }
        return;
      }

      setIsRegistered(true);
    };

    const syncAuthFromSameTab = () => {
      const token = getStoredToken();
      setIsRegistered(Boolean(token));
      if (!token) {
        setUserRole(null);
      }
    };

    window.addEventListener('storage', syncAuthFromOtherTab);
    window.addEventListener('carbon-auth-changed', syncAuthFromSameTab);
    return () => {
      window.removeEventListener('storage', syncAuthFromOtherTab);
      window.removeEventListener('carbon-auth-changed', syncAuthFromSameTab);
    };
  }, [navigate, persistRegistrationData]);

  const completeRegistration = useCallback(
    (data: MSMERegistrationData, token?: string) => {
      persistRegistrationData(data);

      if (token) {
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('token', token);
            notifyAuthTokenChanged();
          } catch (error) {
            console.warn('Unable to persist authentication token.', error);
          }
        }

        setIsRegistered(true);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('carbon-auth-changed'));
        }
      }
    },
    [persistRegistrationData]
  );

  const setAccountPathway = useCallback((pathway: AccountPathway) => {
    setAccountPathwayState(pathway);
    setStoredAccountPathway(pathway);
  }, []);

  const completeLoginFromResponse = useCallback(
    async (response: any): Promise<LoginResult> => {
      if (!response?.success) {
        throw new Error(response?.message || 'Invalid credentials.');
      }

      if (response?.data?.loginOtpRequired) {
        // BUG-001: OTP challenge must not keep a prior registered session alive.
        setIsRegistered(false);
        setUserRole(null);
        return {
          status: 'login_otp_required',
          sessionId: String(response.data.sessionId || ''),
          channels: resolveOtpChannels(response.data.channels),
          message: response.message,
        };
      }

      if (response?.data?.mfaRequired) {
        return {
          status: 'mfa_required',
          mfaToken: String(response.data.mfaToken || ''),
          message: response.message,
        };
      }

      if (response?.data?.mfaEnrollmentRequired) {
        return {
          status: 'mfa_enrollment_required',
          enrollmentToken: String(response.data.enrollmentToken || ''),
          message: response.message,
        };
      }

      const token = response?.data?.token;
      const role = response?.data?.user?.role as string | undefined;
      const user = response?.data?.user;
      const useCookieAuth = Boolean(response?.data?.useCookieAuth);
      const persistToken = (useCookieAuth && !isInsecureHttpOrigin()) ? null : token;

      applyAuthSessionFromResponse({
        token,
        useCookieAuth,
        csrfToken: response?.data?.csrfToken,
        user,
      });

      if (!token && !useCookieAuth) {
        throw new Error('Login failed. No authentication session returned.');
      }

      if (role === 'admin' || role === 'view') {
        if (user) {
          persistAdminPortalSession(user, persistToken);
        } else if (typeof window !== 'undefined' && persistToken) {
          localStorage.setItem('token', persistToken);
          notifyAuthTokenChanged();
        }
        setIsRegistered(true);
        setUserRole(role);
        return { status: 'authenticated', redirectPath: POST_LOGIN_ROUTES[role] };
      }

      if (role === 'support' || role === 'expert') {
        if (user) {
          persistAdminPortalSession(user, persistToken);
        } else if (typeof window !== 'undefined' && persistToken) {
          localStorage.setItem('token', persistToken);
          notifyAuthTokenChanged();
        }
        setIsRegistered(true);
        setUserRole(role);
        return { status: 'authenticated', redirectPath: POST_LOGIN_ROUTES[role] };
      }

      if (role === 'partner') {
        const partner = response?.data?.partner;
        if (!user || !partner) {
          throw new Error('No active partner application is linked to this account.');
        }
        persistPartnerPortalSession(user, partner, persistToken);
        setIsRegistered(true);
        setUserRole(role);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('carbon-auth-changed'));
        }
        return { status: 'authenticated', redirectPath: POST_LOGIN_ROUTES.partner };
      }

      if (typeof window !== 'undefined' && persistToken) {
        try {
          localStorage.setItem('token', persistToken);
          notifyAuthTokenChanged();
        } catch (error) {
          console.warn('Unable to persist authentication token.', error);
        }
      }

      setIsRegistered(true);
      setUserRole(role || null);
      setAccountPathway(role === 'enterprise' ? 'enterprise' : 'msme');

      if (response?.data?.msme) {
        const normalized = mapMsmeToRegistrationData(response.data.msme);
        if (normalized) {
          persistRegistrationData(normalized);
        }
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('carbon-auth-changed'));
      }

      return { status: 'authenticated', redirectPath: null };
    },
    [persistRegistrationData, setAccountPathway]
  );

  const login = useCallback(
    async (email: string, password: string, _pathway?: AccountPathway): Promise<LoginResult> => {
      try {
        clearStoredAuthState();
        const response = await ApiService.login(email, password);
        return completeLoginFromResponse(response);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Login failed. Please try again.';
        throw new Error(message);
      }
    },
    [completeLoginFromResponse]
  );

  const logout = useCallback((options?: LogoutOptions) => {
    const redirectTo = options?.redirectTo ?? '/';

    setIsLoggingOut(true);
    void (async () => {
      try {
        await ApiService.logout();
      } catch {
        // proceed with local cleanup even if server logout fails
      }
      clearStoredAuthState();
      setIsRegistered(false);
      setUserRole(null);
      setAccountPathwayState(null);
      persistRegistrationData(null);
      queryClient.clear();

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('carbon-auth-changed'));
      }

      navigate(redirectTo, { replace: true });

      window.requestAnimationFrame(() => {
        setIsLoggingOut(false);
      });
    })();
  }, [navigate, persistRegistrationData]);

  const resetRegistration = useCallback(() => {
    persistRegistrationData(null);
    setIsRegistered(false);

    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem('token');
      notifyAuthTokenChanged();
      clearApiRequestCache();
      localStorage.removeItem('msmeRegistration');
    } catch (error) {
      console.warn('Unable to clear stored registration state.', error);
    }
  }, [persistRegistrationData]);

  const value = useMemo(
    () => ({
      isRegistered,
      isLoggingOut,
      hasCompletedRegistration,
      registrationData,
      accountPathway,
      userRole,
      completeRegistration,
      login,
      completeLoginFromResponse,
      setAccountPathway,
      logout,
      resetRegistration,
    }),
    [
      isRegistered,
      isLoggingOut,
      hasCompletedRegistration,
      registrationData,
      accountPathway,
      userRole,
      completeRegistration,
      login,
      completeLoginFromResponse,
      setAccountPathway,
      logout,
      resetRegistration,
    ]
  );

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
};

export const useRegistration = (): RegistrationContextValue => {
  const context = useContext(RegistrationContext);

  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }

  return context;
};

